import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, input, type OnInit, type OnDestroy, signal, computed } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import _ from 'lodash';
import { InputParameter, InputParameterFormGroup } from '../../../models/engineering/input-parameter';
import { Parameter } from '../../../models/engineering/parameter';
import { ModalService } from '@card-software/library';
import { ParameterProxyService } from '../../../proxies/item/parameter.proxy.service';
import { ParameterValuesModalComponent } from '../../modals/step-values-modal/parameter-values-modal.component';
import { parameterValueTypeValidator, getParameterTypeErrorMessage } from '../../../validators/parameter-type.validator';
import { ValueAccessorDirective } from '../../../directives/value-accessor.directive';

@Component({
  selector: 'app-parameter-details-viewer',
  templateUrl: './parameter-details-viewer.component.html',
  styleUrl: './parameter-details-viewer.component.scss',
  hostDirectives: [ValueAccessorDirective],
})
export class ParameterDetailsViewerComponent implements OnInit, OnDestroy {
  readonly canEdit = input<boolean>(false);

  private readonly valueAccessor = inject(ValueAccessorDirective<InputParameter[]>);
  private readonly modalService = inject(ModalService);
  private readonly parameterProxyService = inject(ParameterProxyService);

  readonly allParameters = signal<Parameter[]>([]);
  readonly getParameterTypeErrorMessage = getParameterTypeErrorMessage;

  additionalParameters = new FormArray<FormGroup<InputParameterFormGroup>>([]);

  formGroup = new FormGroup({
    additionalParameters: this.additionalParameters,
  });

  readonly parameterCount = computed(() => this.additionalParameters.length);

  constructor() {
    this.onValueChanges();
  }

  private onValueChanges(): void {
    this.valueAccessor.value$.subscribe((data: InputParameter[]) => {
      if (data && Array.isArray(data) && data.length > 0) {
        this.additionalParameters.clear();
        data.forEach((param: InputParameter) => {
          const paramGroup = this.createParameterFormGroup(param);
          this.additionalParameters.push(paramGroup);
        });
      }
    });

    this.additionalParameters.valueChanges.subscribe(() => {
      const parametersArray: InputParameter[] = this.additionalParameters.controls.map((control) => ({
        isActive: control.value.isActive ?? false,
        parameterRef: control.value.parameterRef ?? '',
        hasMultipleValues: control.value.hasMultipleValues ?? false,
        isRequired: control.value.isRequired ?? false,
        values: control.value.values ?? [],
      }));

      this.valueAccessor.notifyValueChange(parametersArray);
      this.valueAccessor.notifyTouched();
    });
  }

  ngOnInit(): void {
    this.loadAllParameters();
  }

  ngOnDestroy(): void {
    this.additionalParameters.clear();
  }

  private loadAllParameters(): void {
    this.parameterProxyService.getParameters().subscribe({
      next: (response) => {
        this.allParameters.set(response.data || []);
      },
    });
  }

  public addParameter(): void {
    const paramGroup = this.createParameterFormGroup();
    this.additionalParameters.push(paramGroup);
  }

  public createParameterFormGroup(param?: InputParameter): FormGroup<InputParameterFormGroup> {
    const initialValues = this.getInitialParameterValues(param);

    const paramGroup = new FormGroup<InputParameterFormGroup>({
      isActive: new FormControl<boolean>(param?.isActive ?? true, {
        nonNullable: true,
      }),
      parameterRef: new FormControl<string>(param?.parameterRef || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      hasMultipleValues: new FormControl<boolean>(param?.hasMultipleValues || false, { nonNullable: true }),
      isRequired: new FormControl<boolean>(param?.isRequired || false, {
        nonNullable: true,
      }),
      values: new FormArray<FormControl<string>>(
        initialValues.map(
          (value: string) =>
            new FormControl<string>(value, {
              nonNullable: true,
              validators: param?.hasMultipleValues ? [Validators.required] : [],
            }),
        ),
      ),
    });

    this.setupParameterRefChangeHandler(paramGroup);
    return paramGroup;
  }

  private getInitialParameterValues(param?: InputParameter): string[] {
    if (param?.values?.length) {
      return param.values;
    }
    if (param?.hasMultipleValues) {
      return [];
    }
    if (param?.parameterRef) {
      return [''];
    }
    return [];
  }

  private setupParameterRefChangeHandler(paramGroup: FormGroup<InputParameterFormGroup>): void {
    const parameterRefControl = paramGroup.controls.parameterRef;
    const valuesControl = paramGroup.controls.values;
    const hasMultipleValuesControl = paramGroup.controls.hasMultipleValues;

    parameterRefControl.valueChanges.subscribe((value: string) => {
      valuesControl.clear();

      if (!value) {
        valuesControl.disable();
      } else {
        valuesControl.enable();
        const parameter = this.allParameters().find((param) => param.id === value);

        if (hasMultipleValuesControl.value) {
          valuesControl.setValue([]);
        } else {
          const validator = parameter ? parameterValueTypeValidator(parameter.parameterType) : [];
          valuesControl.push(
            new FormControl<string>('', {
              nonNullable: true,
              validators: validator,
            }),
          );
        }
      }
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.additionalParameters.controls, event.previousIndex, event.currentIndex);
    this.additionalParameters.updateValueAndValidity();
  }

  public openStepValuesModal(formGroupIndex: number): void {
    const formGroup = this.additionalParameters.controls[formGroupIndex];
    const originalFormArray = formGroup.controls.values;

    const clonedFormArray = new FormArray<FormControl<string>>(
      _.cloneDeep(originalFormArray.controls).map(
        (control: FormControl<string>) => new FormControl<string>(control.value, { nonNullable: true }),
      ),
    );

    const parameterRef = formGroup.controls.parameterRef.value;
    const parameterType = this.allParameters().find((param) => param.id === parameterRef)?.parameterType;

    this.modalService
      .open(ParameterValuesModalComponent, {}, { stepValues: clonedFormArray, parameterType: parameterType })
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          formGroup.setControl('values', result.data);
        }
      });
  }

  public resetParamValues(parameterGroupFormIndex: number): void {
    const formGroup = this.additionalParameters.controls[parameterGroupFormIndex];
    const valuesControl = formGroup.controls.values;
    const hasMultipleValues = formGroup.controls.hasMultipleValues.value;

    if (hasMultipleValues) {
      valuesControl.clear();
    } else {
      valuesControl.clear();
      valuesControl.push(new FormControl<string>('', { nonNullable: true }));
    }
  }

  public removeParameter(index: number): void {
    this.additionalParameters.removeAt(index);
  }

  public getValuesAsSemicolonSeparated(values: FormArray<FormControl<string>>): string {
    if (!values || values.length === 0) {
      return '';
    }
    return values.controls
      .map((c) => c.value)
      .filter((v) => v)
      .join('; ');
  }
}
